// Güvenlik numarası (9.34.3 · D-696).
//
// AMAÇ. Anahtarları dağıtan sunucu. Sunucu dizine kendi anahtarını koyarsa
// zarflar pekâlâ çözülür, çünkü onları o yazmıştır (D-634). İki kişinin araya
// kimsenin girmediğinden emin olabilmesinin tek yolu, her ikisinin de kendi
// cihazında hesapladığı bir değeri sunucuyu aradan çıkararak — yüz yüze ya da
// başka bir kanaldan — karşılaştırmasıdır.
//
// KAPSAM: iki kişinin ŞU ANKİ BÜTÜN cihazları (D-696, 9.34.3.1). Her cihazın
// kendi kimlik anahtarı var; mesaj her birine ayrı şifreleniyor (D-627). Biri
// yeni bir cihaz eklerse numara değişir — doğrusu da bu: o cihaz da artık
// mesajları okuyabiliyor.
//
// BİÇİM: Signal'in sayısal parmak izi düzeni. Her kişi için 30 hane: kimlik
// anahtarları + kullanıcı numarası 5200 kez SHA-512'den geçirilir, ilk 30 bayt
// altı parçada 5 haneye indirilir. İki yarı kullanıcı numarasına göre sıralanır,
// böylece iki telefon da AYNI 60 haneyi aynı sırayla gösterir. Tekrarlı hash,
// numarayı tutturan bir anahtar üretmeyi pahalı kılmak için.
import { sha512 } from '@noble/hashes/sha2.js';
import { base64ToBytes, utf8ToBytes } from './crypto';
import { getSecret, setSecret, deleteSecret, secureStorageAvailable } from './secureStorage';
import { IDENTITY_PUBLIC, safetyVerification } from './storageKeys';
import { api, ApiError } from '../services/api';

const VERSION = new Uint8Array([0, 1]);
const DOMAIN = utf8ToBytes('nosubject-safety-number');
export const SAFETY_ITERATIONS = 5200;
/** QR kodunun içeriği: sürüm öneki + 60 hane. Başka bir QR yanlışlıkla eşleşmesin. */
export const SAFETY_QR_PREFIX = 'nosubject:safety:1:';

function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) { out.set(p, at); at += p.length; }
  return out;
}

/** Bir kişinin 30 hanesi: bütün cihaz kimlik anahtarları, sıradan bağımsız. */
export function fingerprintDigits(userId: number, identityKeys: string[]): string {
  const keys = [...new Set(identityKeys)].sort().map(base64ToBytes);
  if (keys.length === 0) throw new Error('no_identity_keys');
  const material = concat(keys);
  let hash = sha512(concat([VERSION, DOMAIN, utf8ToBytes(String(userId)), material]));
  for (let i = 0; i < SAFETY_ITERATIONS; i++) hash = sha512(concat([hash, material]));
  let digits = '';
  for (let chunk = 0; chunk < 6; chunk++) {
    // 5 bayt = 40 bit; Number 53 bite kadar kesin, taşma yok.
    let value = 0;
    for (let b = 0; b < 5; b++) value = value * 256 + hash[chunk * 5 + b];
    digits += String(value % 100000).padStart(5, '0');
  }
  return digits;
}

export type Party = { userId: number; identityKeys: string[] };

/** İki kişinin ortak numarası: 60 hane, küçük kullanıcı numarası önde. */
export function safetyNumber(a: Party, b: Party): string {
  const [first, second] = a.userId <= b.userId ? [a, b] : [b, a];
  return fingerprintDigits(first.userId, first.identityKeys)
    + fingerprintDigits(second.userId, second.identityKeys);
}

/** Ekranda gösterim: 5'erli 12 grup. */
export function safetyGroups(digits: string): string[] {
  return digits.match(/\d{5}/g) ?? [];
}

export function safetyQrPayload(digits: string): string {
  return `${SAFETY_QR_PREFIX}${digits}`;
}

/** Okutulan QR bu sohbetin numarasıyla eşleşiyor mu. */
export function qrMatches(scanned: string, digits: string): boolean {
  return scanned.trim() === safetyQrPayload(digits);
}

// --- "Doğrulandı" işareti (9.34.3.3) -------------------------------------
//
// Yalnız bu cihazda, sunucuda değil: doğrulamanın amacı sunucuya güvenmek
// zorunda kalmamak. Doğrulanan NUMARA saklanıyor, bir evet/hayır değil; numara
// değişince işaret kendiliğinden düşüyor, ayrıca silmeye gerek kalmıyor.

export type Verification = { number: string; at: string };

export async function verificationFor(peerId: number): Promise<Verification | null> {
  const raw = await getSecret(safetyVerification(peerId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Verification;
    return typeof parsed?.number === 'string' && /^\d{60}$/.test(parsed.number) ? parsed : null;
  } catch {
    return null;
  }
}

export async function markVerified(peerId: number, digits: string, at = new Date()): Promise<void> {
  if (!/^\d{60}$/.test(digits)) throw new Error('bad_safety_number');
  await setSecret(safetyVerification(peerId), JSON.stringify({ number: digits, at: at.toISOString() }));
}

export function clearVerified(peerId: number): Promise<void> {
  return deleteSecret(safetyVerification(peerId));
}

export function isVerified(record: Verification | null, digits: string | null): boolean {
  return !!record && !!digits && record.number === digits;
}

// --- Numarayı dizinden hesaplamak -----------------------------------------

/**
 * - `ready`: iki tarafın cihaz listesi alındı, numara hesaplandı.
 * - `peer`: karşı tarafın dizinde cihazı yok (eski sürüm); şifreleme açık değil.
 * - `self`: bu cihazın kimliği yok ya da dizinde görünmüyor; numara bu cihazı
 *   kapsamazdı, göstermek yanıltıcı olurdu.
 * - `error`: ağ hatası.
 */
export type SafetyState =
  | { state: 'ready'; digits: string }
  | { state: 'peer' | 'self' | 'error' };

export async function loadSafetyNumber(
  token: string,
  myId: number,
  peerId: number,
  deps: { list?: typeof api.fetchDeviceList; myIdentity?: () => Promise<string | null> } = {},
): Promise<SafetyState> {
  const list = deps.list ?? api.fetchDeviceList;
  const myIdentity = deps.myIdentity ?? (() => getSecret(IDENTITY_PUBLIC));
  if (!deps.myIdentity && !secureStorageAvailable()) return { state: 'self' };
  const own = await myIdentity();
  if (!own) return { state: 'self' };
  let peerKeys: string[];
  try {
    peerKeys = (await list(token, peerId)).devices.map((d) => d.identity_key);
  } catch (error) {
    return { state: error instanceof ApiError && error.status === 404 ? 'peer' : 'error' };
  }
  if (peerKeys.length === 0) return { state: 'peer' };
  let myKeys: string[];
  try {
    myKeys = (await list(token, myId)).devices.map((d) => d.identity_key);
  } catch (error) {
    return { state: error instanceof ApiError && error.status === 404 ? 'self' : 'error' };
  }
  // Dizin bu cihazı listelemiyorsa numara bu cihazı kapsamaz.
  if (!myKeys.includes(own)) return { state: 'self' };
  return { state: 'ready', digits: safetyNumber({ userId: myId, identityKeys: myKeys }, { userId: peerId, identityKeys: peerKeys }) };
}
